import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Monitor, Unlink } from 'lucide-react';

interface PromotionActionsProps {
    selectedCount: number;
    onAddToScreen: () => void;
    onRemoveFromScreen?: () => void;
    onNewPromotion: () => void;
    onImport: (data: any) => void;
    onSync: () => void;
    promotions: any[];
    isSyncing: boolean;
}

const STICKY_MARGIN = 12;
const STICKY_GAP = 12;
const MIN_LEFT_GUTTER = 168;

export const PromotionActions: React.FC<PromotionActionsProps> = ({
    selectedCount,
    onAddToScreen,
    onRemoveFromScreen,
    onNewPromotion,
    onImport,
    onSync,
    promotions,
    isSyncing
}) => {
    const buttonsRef = useRef<HTMLDivElement>(null);
    const [showStickyButtons, setShowStickyButtons] = useState(false);
    const [stickyStyle, setStickyStyle] = useState<React.CSSProperties>({
        top: '5.25rem',
        right: STICKY_MARGIN
    });

    useEffect(() => {
        if (selectedCount === 0) {
            setShowStickyButtons(false);
            return;
        }

        const updateStickyPosition = () => {
            const nav = document.querySelector('nav.sticky') as HTMLElement | null;
            const navBottom = nav?.getBoundingClientRect().bottom ?? 64;
            const top = Math.max(navBottom + STICKY_MARGIN, STICKY_MARGIN);

            const table = document.getElementById('promotion-table-container');
            const tableLeft = table?.getBoundingClientRect().left ?? STICKY_MARGIN;
            const viewportWidth = window.innerWidth;
            const leftGutter = tableLeft - STICKY_MARGIN - STICKY_GAP;

            if (leftGutter >= MIN_LEFT_GUTTER) {
                setStickyStyle({
                    top,
                    left: 'auto',
                    right: Math.max(STICKY_MARGIN, viewportWidth - tableLeft + STICKY_GAP),
                    width: 'max-content',
                    maxWidth: leftGutter
                });
                return;
            }

            setStickyStyle({
                top,
                left: 'auto',
                right: STICKY_MARGIN,
                width: 'max-content',
                maxWidth: Math.min(280, viewportWidth - STICKY_MARGIN * 2)
            });
        };

        updateStickyPosition();

        const originalButtons = buttonsRef.current;
        if (!originalButtons) {
            return;
        }

        const buttonsObserver = new IntersectionObserver(
            ([entry]) => {
                setShowStickyButtons(!entry.isIntersecting);
            },
            {
                threshold: 0,
                rootMargin: '0px 0px -48px 0px'
            }
        );
        buttonsObserver.observe(originalButtons);

        const tableContainer = document.getElementById('promotion-table-container');
        const resizeObserver = tableContainer ? new ResizeObserver(updateStickyPosition) : null;
        if (tableContainer && resizeObserver) {
            resizeObserver.observe(tableContainer);
        }

        window.addEventListener('resize', updateStickyPosition);
        window.visualViewport?.addEventListener('resize', updateStickyPosition);

        return () => {
            buttonsObserver.disconnect();
            resizeObserver?.disconnect();
            window.removeEventListener('resize', updateStickyPosition);
            window.visualViewport?.removeEventListener('resize', updateStickyPosition);
        };
    }, [selectedCount]);

    return (
        <>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 gap-3 sm:gap-0">
                <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Produtos</h2>
                    <p className="text-gray-600 text-sm sm:text-base mt-1">Gerencie seus produtos e adicione-os às telas</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto" ref={buttonsRef}>
                    {selectedCount > 0 && (
                        <>
                            <button
                                onClick={onAddToScreen}
                                className="bg-green-500 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center gap-2 text-sm sm:text-base"
                            >
                                <Monitor size={18} className="sm:w-[20px] sm:h-[20px]" />
                                <span className="whitespace-nowrap">Vincular {selectedCount} {selectedCount === 1 ? 'promoção' : 'promoções'}</span>
                            </button>
                            {onRemoveFromScreen && (
                                <button
                                    onClick={onRemoveFromScreen}
                                    className="bg-red-500 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-red-600 transition-colors flex items-center justify-center gap-2 text-sm sm:text-base"
                                >
                                    <Unlink size={18} className="sm:w-[20px] sm:h-[20px]" />
                                    <span className="whitespace-nowrap">Desvincular {selectedCount} {selectedCount === 1 ? 'promoção' : 'promoções'}</span>
                                </button>
                            )}
                        </>
                    )}

                </div>
            </div>

            {selectedCount > 0 && createPortal(
                <div
                    className="fixed z-40 flex flex-col gap-2"
                    style={{
                        ...stickyStyle,
                        opacity: showStickyButtons ? 1 : 0,
                        pointerEvents: showStickyButtons ? 'auto' : 'none',
                        transition: 'opacity 0.2s ease-in-out, top 0.2s ease-in-out, right 0.2s ease-in-out'
                    }}
                >
                    <button
                        type="button"
                        onClick={onAddToScreen}
                        className="max-w-full min-w-0 bg-green-500 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2 shadow-xl text-sm sm:text-base"
                    >
                        <Monitor size={18} className="flex-shrink-0 sm:w-[20px] sm:h-[20px]" />
                        <span className="text-center flex-1 min-w-0 break-words">
                            Vincular {selectedCount} {selectedCount === 1 ? 'promoção' : 'promoções'}
                        </span>
                    </button>
                    {onRemoveFromScreen && (
                        <button
                            type="button"
                            onClick={onRemoveFromScreen}
                            className="max-w-full min-w-0 bg-red-500 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-red-600 transition-colors flex items-center gap-2 shadow-xl text-sm sm:text-base"
                        >
                            <Unlink size={18} className="flex-shrink-0 sm:w-[20px] sm:h-[20px]" />
                            <span className="text-center flex-1 min-w-0 break-words">
                                Desvincular {selectedCount} {selectedCount === 1 ? 'promoção' : 'promoções'}
                            </span>
                        </button>
                    )}
                </div>,
                document.body
            )}
        </>
    );
};
